import React, { useMemo, useState } from 'react';
import { Table, Button, Popconfirm, Input, Space } from 'antd';
import { useLoanRepayment } from '../../store/loanRepaymentContext';
import { LoanRepayment } from '../../types/loanRepayment';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import PaymentInvoiceHandler from '../Print/PaymentInvoiceHandler';
import PageNavigation from '../Layout/PageNavigation';

interface Props {
    onEdit: (loanRepayment: LoanRepayment) => void;
}

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
    { value: "other", label: "Other" },
];

const LoanRepaymentList: React.FC<Props> = ({ onEdit }) => {
    const { loanRepayments, deleteLoanRepayment } = useLoanRepayment();
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');

    // Helper functions for customer name and payment method label
    const getCustomerName = (customerId?: string, customerName?: string) => {
        // If you want to fetch from customers context, add it here.
        return customerName || "-";
    };
    const getPaymentMethodLabel = (code?: string) => {
        return PAYMENT_METHODS.find(m => m.value === code)?.label || "-";
    };

    const filtered = useMemo(() => {
        return loanRepayments.filter(r =>
            (r.repaymentNumber ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.customerName ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (r.customerPhone ?? '').toLowerCase().includes(search.toLowerCase())
        );
    }, [loanRepayments, search]);

    const columns = [
        { title: t('repaymentNumber'), dataIndex: 'repaymentNumber' },
        { title: t('customer'), dataIndex: 'customerName' },
        { title: t('customerPhone'), dataIndex: 'customerPhone' },
        { title: t('date'), dataIndex: 'date', render: (d: string) => new Date(d).toLocaleDateString() },
        { title: t('amount'), dataIndex: 'amount' },
        { title: t('currency'), dataIndex: 'currency' },
        { title: t('paymentMethod'), dataIndex: 'paymentMethod' },
        { title: t('reference'), dataIndex: 'reference' },
        { title: t('note'), dataIndex: 'note' },
        {
            title: t('actions'),
            render: (_: any, record: LoanRepayment) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => onEdit(record)}>{t('edit')}</Button>
                    <Popconfirm
                        title={t('sure_to_delete') as string}
                        onConfirm={() => deleteLoanRepayment(record.id!)}
                    >
                        <Button danger icon={<DeleteOutlined />}>{t('delete')}</Button>
                    </Popconfirm>
                    <PaymentInvoiceHandler
                        type="loan"
                        invoice={record}
                        getPartyName={() =>
                            getCustomerName(record.customer, record.customerName)
                        }
                        getPaymentMethodLabel={getPaymentMethodLabel}
                        buttonText={t('print_invoice') as string}
                    />
                </Space>
            )
        }
    ];

    return (
        <div>
            <PageNavigation title={t('loan_repayments') as string} />
            <Input.Search
                allowClear
                style={{ marginBottom: 16, maxWidth: 300 }}
                placeholder={t('search_repayments') as string}
                onChange={e => setSearch(e.target.value)}
                value={search}
            />
            <Table
                rowKey={record => record.id as string}
                dataSource={filtered}
                columns={columns}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default LoanRepaymentList;
